/**
 * AlertsPanel — operator-facing inbox of system alerts.
 *
 * The backend's audit verifier writes a `chain_broken` alert when it sees
 * a hash mismatch (Wave 1); future workers will write agent-offline,
 * provider-down, etc. This panel is where the operator sees them and
 * acks them resolved.
 */

import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

import { ApiError, api, type AlertItem } from "../lib/api";

const SEVERITY_COLOR: Record<string, string> = {
  critical: "red",
  high: "orange",
  warning: "yellow",
  info: "blue",
};

export function AlertsPanel() {
  const [items, setItems] = useState<AlertItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const resp = await api.alerts(onlyOpen);
      setItems(resp.alerts);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "fetch failed");
    }
  }, [onlyOpen]);

  useEffect(() => {
    refresh();
    const i = window.setInterval(refresh, 15000);
    return () => window.clearInterval(i);
  }, [refresh]);

  const resolve = async (alert_id: string) => {
    setBusy(alert_id);
    try {
      await api.alertResolve(alert_id);
      await refresh();
    } finally {
      setBusy(null);
    }
  };

  if (error) return <Alert color="red">{error}</Alert>;
  if (items === null) return <Loader size="sm" />;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Alerts</Title>
        <Group>
          <Switch
            checked={onlyOpen}
            onChange={(e) => setOnlyOpen(e.currentTarget.checked)}
            label="Only open"
          />
          <Button variant="light" onClick={refresh}>
            Refresh
          </Button>
        </Group>
      </Group>

      {items.length === 0 ? (
        <Paper p="xl" withBorder>
          <Text c="dimmed">
            {onlyOpen ? "No open alerts." : "No alerts on record."}
          </Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Severity</Table.Th>
                <Table.Th>Kind</Table.Th>
                <Table.Th>Account</Table.Th>
                <Table.Th>Body</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((a) => (
                <Table.Tr key={a.alert_id}>
                  <Table.Td>
                    <Badge color={SEVERITY_COLOR[a.severity] ?? "gray"}>
                      {a.severity}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{a.kind}</Table.Td>
                  <Table.Td style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {a.account_id ? a.account_id.slice(0, 12) + "…" : "—"}
                  </Table.Td>
                  <Table.Td dir="rtl">{a.body_he}</Table.Td>
                  <Table.Td>{new Date(a.created_at).toLocaleString()}</Table.Td>
                  <Table.Td>
                    {a.resolved_at ? (
                      <Badge color="green" variant="light">
                        Resolved
                      </Badge>
                    ) : (
                      <Badge color="red" variant="light">
                        Open
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {a.resolved_at === null && (
                      <Button
                        size="xs"
                        loading={busy === a.alert_id}
                        onClick={() => resolve(a.alert_id)}
                      >
                        Resolve
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
